import { useTranslation } from 'react-i18next'
import Avatar from 'react-avatar'
import PropTypes from 'prop-types'

import Btn from '../../../../../UI/Btn'
import BtnTooltip from '../../../../../UI/BtnTooltip'
import { ContactLines } from './personIcons'
import eventEmitter, { types } from '../../../../../shared/eventEmitter'

import styles from '../style.module.scss'

/**
 * Abschnitt „Schule & Lehrkraefte" in den Basisinformationen.
 *
 * Die Schule haengt an der betreuenden Lehrkraft — sie steht deshalb neben ihr
 * und nicht als eigener Punkt irgendwo anders. Beides gehoert zu den
 * Stammdaten des Unternehmens, nicht zu seinem Team.
 *
 * „Suchen" und „Einladen" oeffnen dasselbe Fenster (`AddTeacherModal`): dort
 * laesst sich eine vorhandene Lehrkraft zuweisen oder eine neue per E-Mail
 * einladen. Beides ist Sache der Verwaltung; mit `readOnly` (Sicht der
 * Lehrkraft) bleiben die Angaben und die Erreichbarkeit stehen, die Handlungen
 * fallen weg — auch der Weg ins Profil einer Kolleg:in, das eine Lehrkraft
 * nicht zu bearbeiten hat.
 */
export default function SchoolTeachersSection({ companyId, supervision, teachers, avatarUrls, readOnly }) {
	const { t } = useTranslation()

	const openTeacherDialog = () => eventEmitter.emit(types.openAddTeacherModal, companyId)
	const school = supervision?.Teacher?.School

	return (
		<section className={styles.block}>
			<div className={styles.blockHead}>
				<h3 className={styles.blockTitle}>{t('School and teachers')}</h3>
				{!readOnly && (
					<div className={styles.blockActions}>
						<Btn
							className={styles.btnVerify}
							iconClassName={styles.btnIconReset}
							type="button"
							icon="search"
							iconOrder={0}
							iconSize={{ w: 17, h: 17 }}
							iconColor="#008b9c"
							onClick={openTeacherDialog}
						>
							{t('Search')}
						</Btn>
						<Btn
							className={styles.btnVerify}
							iconClassName={styles.btnIconReset}
							type="button"
							icon="plus"
							iconOrder={0}
							iconSize={{ w: 13, h: 17 }}
							iconColor="#008b9c"
							onClick={openTeacherDialog}
						>
							{t('Invite')}
						</Btn>
					</div>
				)}
			</div>

			<div className={styles.fieldGrid}>
				<div className={styles.readOnlyInput}>
					<h3 className={styles.readOnlyLabel}>{t('school')}</h3>
					<div className={styles.readOnlyText}>
						{school?.name || <span className={styles.emptyValue}>—</span>}
					</div>
				</div>
				{!!school?.location && (
					<div className={styles.readOnlyInput}>
						<h3 className={styles.readOnlyLabel}>{t('location')}</h3>
						<div className={styles.readOnlyText}>
							{[school.streetName, school.number].filter(Boolean).join(' ')}
							{school.streetName ? ', ' : ''}
							{[school.zipCode, school.location].filter(Boolean).join(' ')}
						</div>
					</div>
				)}
			</div>

			<div className={styles.peopleGrid}>
				<div className={styles.contactCard}>
					<span className={styles.employeeProfile}>
						<Avatar
							className={styles.userAvatar}
							round
							size={32}
							name={`${supervision?.Teacher?.firstName || ''} ${supervision?.Teacher?.lastName || ''}`}
							color="#7b8fa8"
							src={avatarUrls[supervision?.Teacher?.avatarHashName] || ''}
						/>
						<p className={styles.name}>
							{supervision?.Teacher
								? `${supervision.Teacher.firstName} ${supervision.Teacher.lastName}`
								: '—'}
						</p>
						{supervision?.Teacher && !readOnly && (
							<BtnTooltip
								items={[
									{
										label: t('Edit Userprofile'),
										onClick: () =>
											eventEmitter.emit(types.openStudentProfile, supervision.Teacher, () => {})
									}
								]}
							/>
						)}
					</span>
					<span className={styles.roleTag}>{t('Supervision Teacher')}</span>
					<ContactLines email={supervision?.Teacher?.email} phone={supervision?.Teacher?.phone} />
				</div>

				{teachers?.map(item => (
					<div key={item.teacherId} className={styles.contactCard}>
						<span className={styles.employeeProfile}>
							<Avatar
								className={styles.userAvatar}
								round
								size={32}
								name={`${item.Teacher.firstName} ${item.Teacher.lastName}`}
								color="#7b8fa8"
								src={avatarUrls[item.Teacher.avatarHashName] || ''}
							/>
							<p className={styles.name}>
								{item.Teacher.firstName} {item.Teacher.lastName}
							</p>
							{!readOnly && (
								<BtnTooltip
									items={[
										{
											label: t('Edit Userprofile'),
											onClick: () =>
												eventEmitter.emit(types.openStudentProfile, item.Teacher, () => {})
										}
									]}
								/>
							)}
						</span>
						<span className={styles.roleTag}>{t('Teacher')}</span>
						<ContactLines email={item.Teacher.email} phone={item.Teacher.phone} />
					</div>
				))}
			</div>
		</section>
	)
}

SchoolTeachersSection.propTypes = {
	companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	readOnly: PropTypes.bool,
	supervision: PropTypes.object,
	teachers: PropTypes.array,
	avatarUrls: PropTypes.object.isRequired
}
